<template>
  <div class="admin-page">
    <div class="admin-shell">
      <div class="admin-heading">
        <div>
          <h1>System Admin</h1>
          <p>Manage users and inspect the overall state of the platform.</p>
        </div>
        <button class="btn btn-primary" @click="loadAdminData" :disabled="loading || !authorized">
          Refresh
        </button>
      </div>

      <div v-if="loading" class="admin-message">Loading admin tools...</div>
      <div v-else-if="!authorized" class="admin-message admin-error">
        You do not have access to this page.
      </div>
      <div v-else>
        <div class="admin-summary">
          <div class="summary-card">
            <span class="summary-label">Users</span>
            <strong>{{ users.length }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Accounts</span>
            <strong>{{ accounts.length }}</strong>
          </div>
          <div class="summary-card">
            <span class="summary-label">Scorecards</span>
            <strong>{{ scorecards.length }}</strong>
          </div>
        </div>

        <div class="admin-toolbar">
          <div class="tab-row">
            <span
              class="admin-tab-btn"
              :class="{ active: activeTab === 'users' }"
              role="button"
              tabindex="0"
              @click="activeTab = 'users'"
              @keydown.enter.prevent="activeTab = 'users'"
              @keydown.space.prevent="activeTab = 'users'"
            >Users</span>
            <span
              class="admin-tab-btn"
              :class="{ active: activeTab === 'accounts' }"
              role="button"
              tabindex="0"
              @click="activeTab = 'accounts'"
              @keydown.enter.prevent="activeTab = 'accounts'"
              @keydown.space.prevent="activeTab = 'accounts'"
            >Accounts</span>
            <span
              class="admin-tab-btn"
              :class="{ active: activeTab === 'scorecards' }"
              role="button"
              tabindex="0"
              @click="activeTab = 'scorecards'"
              @keydown.enter.prevent="activeTab = 'scorecards'"
              @keydown.space.prevent="activeTab = 'scorecards'"
            >Scorecards</span>
          </div>
          <div class="toolbar-controls">
            <input
              v-model.trim="search"
              class="search-input"
              type="search"
              :placeholder="searchPlaceholder"
            />
            <label v-if="activeTab === 'users'" class="active-toggle">
              <input v-model="showOnlyActiveUsers" type="checkbox" />
              <span>Active</span>
            </label>
          </div>
        </div>

        <div v-if="activeTab === 'users'" class="users-toolbar">
          <div class="secondary">
            Showing {{ filteredUsers.length }} of {{ users.length }} users
          </div>
        </div>

        <div v-if="error_message" class="admin-message admin-error">{{ error_message }}</div>

        <div v-if="activeTab === 'users'" class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Flags</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in filteredUsers" :key="user.user_id">
                <td>
                  <div class="primary">{{ user.first_name }} {{ user.last_name }}</div>
                  <div class="secondary">#{{ user.user_id }} • {{ user.email }}</div>
                </td>
                <td>
                  <span class="pill" :class="'state-' + user.state">{{ stateLabel(user.state) }}</span>
                </td>
                <td>
                  <div class="secondary">{{ user.deleted ? 'Deleted' : 'Active record' }}</div>
                  <div class="secondary">{{ user.consultant ? 'Consultant' : 'Standard user' }}</div>
                </td>
                <td class="secondary">{{ formatDate(user.last_login) }}</td>
                <td>
                  <div class="action-row">
                    <button class="mini-btn" @click="setUserState(user, 0)" :disabled="savingUserId === user.user_id">Created</button>
                    <button class="mini-btn" @click="setUserState(user, 1)" :disabled="savingUserId === user.user_id">Verified</button>
                    <button class="mini-btn" @click="setUserState(user, 2)" :disabled="savingUserId === user.user_id">Expired</button>
                    <button class="mini-btn blocked" @click="setUserState(user, 3)" :disabled="savingUserId === user.user_id || cannotBlockUser(user)">Blocked</button>
                    <button class="mini-btn" @click="toggleConsultant(user)" :disabled="savingUserId === user.user_id">
                      {{ user.consultant ? 'Remove Consultant' : 'Make Consultant' }}
                    </button>
                    <button class="mini-btn danger" @click="toggleDeleted(user)" :disabled="savingUserId === user.user_id || cannotDeleteUser(user)">
                      {{ user.deleted ? 'Restore' : 'Soft Delete' }}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="activeTab === 'accounts'" class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Owner</th>
                <th>Flags</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in filteredAccounts" :key="account.account_id">
                <td>
                  <div class="primary">{{ account.account_name || 'Untitled Account' }}</div>
                  <div class="secondary">#{{ account.account_id }}</div>
                </td>
                <td class="secondary">User #{{ account.user_id }}</td>
                <td>
                  <div class="secondary">{{ account.deleted ? 'Deleted' : 'Active' }}</div>
                  <div class="secondary">{{ account.consulting ? 'Consulting account' : 'Standard account' }}</div>
                </td>
                <td class="secondary">{{ formatDate(account.updated_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="activeTab === 'scorecards'" class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th>Scorecard</th>
                <th>Account</th>
                <th>Type</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="scorecard in filteredScorecards" :key="scorecard.scorecard_id">
                <td>
                  <div class="primary">{{ scorecard.scorecard_name || 'Untitled Scorecard' }}</div>
                  <div class="secondary">#{{ scorecard.scorecard_id }}</div>
                </td>
                <td class="secondary">Account #{{ scorecard.account_id }}</td>
                <td>
                  <div class="secondary">Type #{{ scorecard.scorecard_type_id }}</div>
                  <div class="secondary">{{ scorecard.deleted ? 'Deleted' : 'Active' }}</div>
                </td>
                <td class="secondary">{{ formatDate(scorecard.updated_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { adminService, userService } from '../_services';

export default {
  name: 'admin',
  title: 'System Admin | Foclarity',
  data() {
    return {
      loading: true,
      authorized: false,
      activeTab: 'users',
      showOnlyActiveUsers: false,
      search: '',
      savingUserId: null,
      error_message: '',
      me: null,
      users: [],
      accounts: [],
      scorecards: []
    };
  },
  created() {
    this.loadAdminData();
  },
  computed: {
    searchPlaceholder() {
      if (this.activeTab === 'users') {
        return 'Find a user by id, name, or email';
      }
      if (this.activeTab === 'accounts') {
        return 'Search accounts by id, owner, or name';
      }
      return 'Search scorecards by id, account, or name';
    },
    filteredUsers() {
      const search = this.search.toLowerCase();
      return this.users.filter(user => {
        if (this.showOnlyActiveUsers && user.state === 3) {
          return false;
        }

        const haystack = [
          user.user_id,
          user.first_name,
          user.last_name,
          user.email
        ].join(' ').toLowerCase();
        return haystack.includes(search);
      });
    },
    filteredAccounts() {
      const search = this.search.toLowerCase();
      return this.accounts.filter(account => {
        const haystack = [
          account.account_id,
          account.user_id,
          account.account_name,
          account.account_email
        ].join(' ').toLowerCase();
        return haystack.includes(search);
      });
    },
    filteredScorecards() {
      const search = this.search.toLowerCase();
      return this.scorecards.filter(scorecard => {
        const haystack = [
          scorecard.scorecard_id,
          scorecard.account_id,
          scorecard.scorecard_name,
          scorecard.scorecard_description
        ].join(' ').toLowerCase();
        return haystack.includes(search);
      });
    }
  },
  methods: {
    async loadAdminData() {
      this.loading = true;
      this.error_message = '';
      try {
        this.me = await userService.getMe();
        this.authorized = !!(this.me && this.me.is_super_admin);
        if (!this.authorized) {
          this.loading = false;
          return;
        }

        const [users, accounts, scorecards] = await Promise.all([
          adminService.getUsers(),
          adminService.getAccounts(),
          adminService.getScorecards()
        ]);

        this.users = users;
        this.accounts = accounts;
        this.scorecards = scorecards;
      } catch (error) {
        this.error_message = error;
      } finally {
        this.loading = false;
      }
    },
    async updateUser(user, payload) {
      this.savingUserId = user.user_id;
      this.error_message = '';
      try {
        const updatedUser = await adminService.updateUser(user.user_id, payload);
        if (updatedUser) {
          const index = this.users.findIndex(item => item.user_id === updatedUser.user_id);
          if (index >= 0) {
            this.$set(this.users, index, updatedUser);
          }
        }
      } catch (error) {
        this.error_message = error;
      } finally {
        this.savingUserId = null;
      }
    },
    setUserState(user, state) {
      if (state === 3 && this.cannotBlockUser(user)) {
        return;
      }
      return this.updateUser(user, { state });
    },
    toggleConsultant(user) {
      return this.updateUser(user, { consultant: !user.consultant });
    },
    toggleDeleted(user) {
      if (this.cannotDeleteUser(user)) {
        return;
      }
      return this.updateUser(user, { deleted: !user.deleted });
    },
    cannotDeleteUser(user) {
      return user && user.user_id === 1 && !user.deleted;
    },
    cannotBlockUser(user) {
      return user && user.user_id === 1;
    },
    stateLabel(state) {
      if (state === 0) return 'Created';
      if (state === 1) return 'Verified';
      if (state === 2) return 'Expired';
      if (state === 3) return 'Blocked';
      return 'Unknown';
    },
    formatDate(value) {
      if (!value) return 'Never';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unknown';
      return date.toLocaleString();
    }
  }
};
</script>

<style scoped lang="scss">
.admin-page {
  flex: 1;
  min-width: 0;
  background: linear-gradient(180deg, #f4f8fb 0%, #ffffff 100%);
}

.admin-shell {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.admin-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 24px;
}

.admin-heading h1 {
  margin: 0 0 8px;
}

.admin-heading p {
  margin: 0;
  color: #466072;
}

.admin-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.summary-card {
  background: #ffffff;
  border: 1px solid #d8e3eb;
  border-radius: 12px;
  padding: 16px;
}

.summary-label {
  display: block;
  color: #5f7788;
  font-size: 13px;
  margin-bottom: 8px;
}

.admin-toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 6px;
  flex-wrap: wrap;
  overflow: visible;
}

.tab-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  overflow: visible;
}

.admin-tab-btn {
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  box-sizing: border-box;
  border: 1px solid #b8cad8;
  border-bottom: 1px solid #b8cad8;
  background: #ffffff !important;
  background-image: none !important;
  background-clip: padding-box;
  color: #21475d !important;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  line-height: 1.1;
  font-size: 14px;
  font-weight: 700;
  text-transform: none;
  overflow: hidden;
  box-shadow: none;
  outline: none;
  user-select: none;
  vertical-align: middle;
  white-space: nowrap;
  isolation: isolate;
}

.admin-tab-btn.active {
  background: transparent !important;
  color: #ffffff !important;
  border-color: #21475d;
  box-shadow: none;
}

.admin-tab-btn.active::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: #21475d;
  z-index: -1;
}

.search-input {
  width: 320px;
  max-width: 100%;
  border: 1px solid #c8d6e1;
  border-radius: 10px;
  padding: 12px 14px;
  background: #ffffff;
}

.toolbar-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.users-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.active-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #355166;
  white-space: nowrap;
}

.active-toggle input {
  width: 16px;
  height: 16px;
}

.admin-message {
  background: #ffffff;
  border: 1px solid #d8e3eb;
  border-radius: 12px;
  padding: 16px;
}

.admin-error {
  border-color: #e2b7b7;
  color: #8a2d2d;
}

.admin-table-wrap {
  background: #ffffff;
  border: 1px solid #d8e3eb;
  border-radius: 16px;
  overflow: auto;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
}

.admin-table th,
.admin-table td {
  padding: 14px 16px;
  border-bottom: 1px solid #edf2f6;
  text-align: left;
  vertical-align: top;
}

.admin-table th {
  background: #f7fafc;
  color: #51697a;
  font-size: 13px;
}

.primary {
  font-weight: 600;
  color: #17364a;
}

.secondary {
  color: #607989;
  font-size: 13px;
  margin-top: 4px;
}

.pill {
  display: inline-block;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
}

.state-0 {
  background: #fff3cd;
  color: #8a6d1d;
}

.state-1 {
  background: #d9f2e3;
  color: #1d6d44;
}

.state-2 {
  background: #f6d8d8;
  color: #8b2d2d;
}

.state-3 {
  background: #2f3b52;
  color: #ffffff;
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.mini-btn {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #bfd0dd;
  border-bottom: 1px solid #bfd0dd;
  background: #ffffff;
  background-image: none;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  color: #21475d;
  box-shadow: none;
  outline: none;
}

.mini-btn.danger {
  border-color: #e0b8b8;
  color: #8a2d2d;
}

.mini-btn.blocked {
  border-color: #5b677f;
  color: #2f3b52;
}

@media (max-width: 900px) {
  .admin-summary {
    grid-template-columns: 1fr;
  }

  .admin-shell {
    padding: 20px 16px 40px;
  }
}
</style>
